import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Wrench, Plus, Trash2, Send, Pencil, BellRing, AlertTriangle, Archive, ArchiveRestore, Search, X } from "lucide-react";
import { toast } from "sonner";
import { RepairReviewDialog, RepairStatusBadge, repairMoney, DueCountdown, RevisitCountdown } from "@/components/RepairReviewDialog";
import { filterActive, HeaderFilterPopover } from "@/components/SheetFilters";

const SERVICE_TYPES = ["Repair", "Overhaul", "Exchange", "Inspection", "Other"];
const CURRENCIES = ["USD", "CAD", "GBP", "EUR"];

const EMPTY_FORM = {
  approver_id: "", date: new Date().toISOString().slice(0, 10), due_date: "", po_ro: "", description: "",
  part_number: "", serial_number: "", fleet_type: "", critical_spares: "No", type_of_service: "Repair",
  currency: "CAD", estimated_cost: "", evaluation_fee: "", turnaround_time: "", avg_repair_cost: "",
  spares_in_stock: "", consumption_rate: "", min_max_details: "", rfq1: "", rfq2: "", rfq3: "", comments: "",
};

const FORM_KEYS = Object.keys(EMPTY_FORM);

const PAGE_SIZE = 30;

const FILTER_DEFS = [
  { label: "Description", key: "description", type: "text" },
  { label: "PO/RO", key: "po_ro", type: "text" },
  { label: "Date", key: "date", type: "daterange" },
  { label: "Est. cost", key: "estimated_cost", type: "numrange" },
  { label: "Requested by", key: "created_by_name", type: "options" },
  { label: "Approver", key: "approver_name", type: "options" },
  { label: "Due", key: "due", type: "options", options: ["Overdue", "Due ≤ 7 days", "Later", "No due date"] },
  { label: "Status", key: "status", type: "options", options: ["pending", "approved", "denied", "revisit"] },
];

const TABLE_COLS = [
  { label: "Estimate", keys: ["description", "po_ro", "date"], width: "" },
  { label: "Cost", keys: ["estimated_cost"], width: "w-[11%]" },
  { label: "People", keys: ["created_by_name", "approver_name"], width: "w-[19%]" },
  { label: "Due", keys: ["due"], width: "w-[13%]" },
  { label: "Status", keys: ["status"], width: "w-[14%]" },
  { label: "", keys: [], width: "w-[11%]" },
];

const dueBucket = (r) => {
  if (!r.due_date) return "No due date";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((new Date(`${r.due_date}T00:00:00`) - today) / 86400000);
  return days < 0 ? "Overdue" : days <= 7 ? "Due ≤ 7 days" : "Later";
};

function RequestFormDialog({ open, onOpenChange, approvers, onSaved, resendReq, editReq }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isResend = !!resendReq;
  const isEdit = !!editReq;
  const src = resendReq || editReq;

  useEffect(() => {
    if (!open) return;
    if (src) {
      const pre = { ...EMPTY_FORM };
      FORM_KEYS.forEach((k) => { if (src[k] !== undefined && src[k] !== null) pre[k] = String(src[k]); });
      pre.approver_id = src.approver_id;
      setForm(pre);
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, resendReq, editReq]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!form.description.trim()) { toast.error("Description is required."); return; }
    if (!isResend && !form.approver_id) { toast.error("Pick who this approval is sent to."); return; }
    setSaving(true);
    try {
      const num = (v) => (v === "" || v === null ? null : Number(v));
      const body = {
        ...form,
        due_date: form.due_date || null,
        estimated_cost: num(form.estimated_cost),
        evaluation_fee: num(form.evaluation_fee),
        avg_repair_cost: num(form.avg_repair_cost),
      };
      if (isEdit) {
        delete body.approver_id;
        await api.patch(`/repair-approvals/${editReq.id}`, body);
        toast.success(`Request updated — ${editReq.approver_name} notified`);
      } else if (isResend) {
        delete body.approver_id;
        await api.post(`/repair-approvals/${resendReq.id}/resend`, body);
        toast.success(`Updated and resent to ${resendReq.approver_name}`);
      } else {
        await api.post("/repair-approvals", body);
        toast.success("Repair approval request sent");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save request");
    } finally {
      setSaving(false);
    }
  };

  const text = (label, key, props = {}) => (
    <div>
      <Label className="text-xs uppercase tracking-widest text-gray-500">{label}</Label>
      <Input className="mt-1" value={form[key]} onChange={(e) => set(key, e.target.value)} data-testid={`repair-field-${key}`} {...props} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="repair-new-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="w-4 h-4" /> {isEdit ? "Edit request" : isResend ? "Edit & resend request" : "Repair quote approval request"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? `Update the details below — it stays pending with ${editReq.approver_name}.`
              : isResend
              ? `Tweak anything below — it goes back to ${resendReq.approver_name} as pending.`
              : "Fill out the estimate details, pick an approver, and it lands in their Tasks."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {src ? (
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Approver</Label>
              <div className="mt-1 h-10 px-3 rounded-md border border-summit-hairline bg-summit-glacier/40 text-sm flex items-center" data-testid="repair-resend-approver">
                {src.approver_name}
              </div>
            </div>
          ) : (
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Send approval to · required</Label>
              <Select value={form.approver_id} onValueChange={(v) => set("approver_id", v)}>
                <SelectTrigger className="mt-1" data-testid="repair-approver-select"><SelectValue placeholder="Pick an approver" /></SelectTrigger>
                <SelectContent>
                  {approvers.map((a) => (
                    <SelectItem key={a.id} value={a.id} data-testid={`repair-approver-option-${a.id}`}>
                      {a.name}{a.job_title ? ` · ${a.job_title}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Date</Label>
            <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)}
              className="w-full h-10 mt-1 px-3 rounded-md border border-summit-hairline text-sm" data-testid="repair-field-date" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-gray-500">Decision due date (optional)</Label>
            <input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)}
              className="w-full h-10 mt-1 px-3 rounded-md border border-summit-hairline text-sm" data-testid="repair-field-due_date" />
            <p className="text-[11px] text-gray-400 mt-1">The approver sees a countdown of days left to decide.</p>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-gray-500">Description · required</Label>
            <Input className="mt-1" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="repair-field-description" />
          </div>
          {text("PO / RO #", "po_ro")}
          {text("Part number", "part_number")}
          {text("Serial number", "serial_number")}
          {text("Fleet type", "fleet_type", { placeholder: "e.g. D228" })}
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Critical spares</Label>
            <Select value={form.critical_spares} onValueChange={(v) => set("critical_spares", v)}>
              <SelectTrigger className="mt-1" data-testid="repair-field-critical-spares"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Type of service</Label>
            <Select value={form.type_of_service} onValueChange={(v) => set("type_of_service", v)}>
              <SelectTrigger className="mt-1" data-testid="repair-field-type-of-service"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SERVICE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Estimated cost</Label>
            <div className="flex gap-2 mt-1">
              <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger className="w-24 shrink-0" data-testid="repair-field-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`repair-currency-option-${c}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input value={form.estimated_cost} onChange={(e) => set("estimated_cost", e.target.value)} type="number" step="0.01" min="0" data-testid="repair-field-estimated_cost" />
            </div>
          </div>
          {text("Evaluation fee if denied", "evaluation_fee", { type: "number", step: "0.01", min: "0" })}
          {text("Turnaround time", "turnaround_time", { placeholder: "e.g. 4-6 weeks" })}
          {text("Average repair cost (RAAS)", "avg_repair_cost", { type: "number", step: "0.01", min: "0" })}
          {text("Current spares in stock", "spares_in_stock")}
          {text("Consumption rate", "consumption_rate")}
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-gray-500">Min / Max details</Label>
            <Input className="mt-1" value={form.min_max_details} onChange={(e) => set("min_max_details", e.target.value)} placeholder="e.g. Min 1 / Max 3" data-testid="repair-field-min_max_details" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-gray-500">RFQs if to procure in the market</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
              <Input value={form.rfq1} onChange={(e) => set("rfq1", e.target.value)} placeholder="RFQ 1" data-testid="repair-field-rfq1" />
              <Input value={form.rfq2} onChange={(e) => set("rfq2", e.target.value)} placeholder="RFQ 2" data-testid="repair-field-rfq2" />
              <Input value={form.rfq3} onChange={(e) => set("rfq3", e.target.value)} placeholder="RFQ 3" data-testid="repair-field-rfq3" />
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-gray-500">Comments</Label>
            <Textarea className="mt-1" rows={3} value={form.comments} onChange={(e) => set("comments", e.target.value)} data-testid="repair-field-comments" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submit} disabled={saving} data-testid="repair-submit-btn">
            {saving ? "Sending…" : (isEdit ? "Save changes" : isResend ? "Save & resend" : "Send for approval")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RepairApprovals() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [approvers, setApprovers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editResend, setEditResend] = useState(null);
  const [editPending, setEditPending] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const [tab, setTab] = useState("active");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [tab, search, filters]);

  const hasAccess = user && (user.repair_approvals_access || user.role === "admin");

  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/repair-approvals"),
        api.get("/repair-approvals/approvers"),
      ]);
      setItems(r1.data || []);
      setApprovers(r2.data || []);
    } catch (e) {
      setItems([]);
      toast.error(e?.response?.data?.detail || "Failed to load repair approvals");
    }
  };
  useEffect(() => { if (hasAccess) load(); }, [hasAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  // Opening a freshly-actioned estimate marks it seen (clears its NEW badge).
  useEffect(() => {
    const r = (items || []).find((x) => x.id === reviewId);
    if (!r || !r.actioned_at || !user || (r.actioned_seen_by || []).includes(user.id)) return;
    api.post(`/repair-approvals/${r.id}/seen`).catch(() => {});
    setItems((prev) => (prev || []).map((x) => (x.id === r.id ? { ...x, actioned_seen_by: [...(x.actioned_seen_by || []), user.id] } : x)));
  }, [reviewId]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (req) => {
    if (!window.confirm(`Delete the request "${req.description}"?`)) return;
    try {
      await api.delete(`/repair-approvals/${req.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const nudge = async (req) => {
    try {
      await api.post(`/repair-approvals/${req.id}/nudge`);
      toast.success(`Nudge sent to ${req.approver_name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send nudge");
    }
  };

  const setActioned = async (req, on) => {
    try {
      await api.post(`/repair-approvals/${req.id}/${on ? "action" : "unaction"}`);
      toast.success(on ? "Moved to Actioned estimates" : "Restored to active estimates");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const awaitingMe = useMemo(() => (items || []).filter((r) => r.approver_id === user?.id && r.status === "pending" && !r.actioned_at), [items, user]);
  const reviewReq = (items || []).find((r) => r.id === reviewId) || null;
  const activeItems = useMemo(() => (items || []).filter((r) => !r.actioned_at), [items]);
  const actionedItems = useMemo(() => (items || []).filter((r) => r.actioned_at), [items]);

  const isFresh = (r) => !!r.actioned_at && !!user && !(r.actioned_seen_by || []).includes(user.id);
  const freshCount = useMemo(() => actionedItems.filter(isFresh).length, [actionedItems, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const nameOptions = useMemo(() => ({
    created_by_name: [...new Set((items || []).map((r) => r.created_by_name).filter(Boolean))].sort(),
    approver_name: [...new Set((items || []).map((r) => r.approver_name).filter(Boolean))].sort(),
  }), [items]);

  const visibleItems = useMemo(() => {
    let rows = tab === "actioned" ? actionedItems : activeItems;
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        [r.description, r.po_ro, r.part_number, r.serial_number, r.fleet_type, r.created_by_name, r.approver_name, r.status, r.comments]
          .some((v) => (v || "").toLowerCase().includes(q)));
    }
    for (const col of FILTER_DEFS) {
      const v = filters[col.key];
      if (!filterActive(col, v)) continue;
      rows = rows.filter((r) => {
        if (col.type === "text") return (r[col.key] || "").toLowerCase().includes(v.trim().toLowerCase());
        if (col.key === "due") return v.includes(dueBucket(r));
        if (col.type === "options") return v.includes(r[col.key]);
        if (col.type === "daterange") return (!v.from || (r.date || "") >= v.from) && (!v.to || (r.date || "") <= v.to);
        if (col.type === "numrange") {
          if (r[col.key] === null || r[col.key] === undefined || r[col.key] === "") return false;
          const n = Number(r[col.key]);
          return (!v.min || n >= Number(v.min)) && (!v.max || n <= Number(v.max));
        }
        return true;
      });
    }
    return rows;
  }, [tab, activeItems, actionedItems, search, filters]);

  const anyFilter = !!search.trim() || FILTER_DEFS.some((c) => filterActive(c, filters[c.key]));

  const totalPages = Math.max(1, Math.ceil(visibleItems.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = useMemo(() => visibleItems.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE), [visibleItems, safePage]);

  // Group by Critical Spares (Yes first), then subgroup by Fleet Type.
  const groups = useMemo(() => {
    const out = [];
    for (const cs of ["Yes", "No"]) {
      const inCs = pageItems.filter((r) => (r.critical_spares === "Yes" ? "Yes" : "No") === cs);
      if (!inCs.length) continue;
      const fleets = {};
      inCs.forEach((r) => {
        const f = (r.fleet_type || "").trim() || "No fleet type";
        (fleets[f] = fleets[f] || []).push(r);
      });
      out.push({ cs, fleets: Object.entries(fleets).sort((a, b) => a[0].localeCompare(b[0])) });
    }
    return out;
  }, [pageItems]);

  if (!user) return null;
  if (!hasAccess) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="repair-no-access">
        <Wrench className="w-8 h-8 mx-auto text-gray-300" />
        <h1 className="font-display text-2xl font-semibold mt-4">Repair Approvals</h1>
        <p className="text-sm text-gray-500 mt-2">You don&apos;t have access to this page. Ask an admin to grant it in Admin Settings.</p>
      </div>
    );
  }

  const isCreatorOrAdmin = (r) => r.created_by === user.id || user.role === "admin";
  const canAction = (r) => r.created_by === user.id || r.approver_id === user.id || user.role === "admin";

  const Row = ({ r }) => (
    <tr className="border-b border-summit-hairline hover:bg-summit-glacier/40 cursor-pointer" onClick={() => setReviewId(r.id)} data-testid={`repair-row-${r.id}`}>
      <td className="px-3 py-2.5 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium truncate">{r.description}</span>
          {isFresh(r) && (
            <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-mono text-[9px] uppercase tracking-widest" data-testid={`repair-fresh-${r.id}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />new
            </span>
          )}
        </div>
        <div className="font-mono text-[11px] text-gray-500 mt-0.5 truncate">
          {r.date}{r.po_ro ? ` · ${r.po_ro}` : ""}
        </div>
      </td>
      <td className="px-3 py-2.5 font-mono text-xs">{repairMoney(r.estimated_cost, r.currency)}</td>
      <td className="px-3 py-2.5">
        <div className="text-xs truncate">{r.created_by_name}</div>
        <div className="text-[11px] text-gray-500 truncate">→ {r.approver_name}</div>
      </td>
      <td className="px-3 py-2.5">
        {r.due_date ? (
          <>
            <div className="font-mono text-[11px] text-gray-500">{r.due_date}</div>
            <div className="mt-0.5"><DueCountdown dueDate={r.due_date} status={r.status} /></div>
          </>
        ) : <span className="text-gray-300 text-xs">—</span>}
      </td>
      <td className="px-3 py-2.5">
        <RepairStatusBadge status={r.status} />
        {r.status === "revisit" && r.revisit_by && (
          <div className="mt-1"><RevisitCountdown req={r} /></div>
        )}
      </td>
      <td className="px-3 py-2.5 text-right whitespace-nowrap">
        {!r.actioned_at && isCreatorOrAdmin(r) && r.status === "pending" && (
          <button
            className="p-1.5 rounded-md text-summit-primary hover:bg-summit-glacier"
            onClick={(e) => { e.stopPropagation(); setEditPending(r); }}
            data-testid={`repair-edit-${r.id}`}
            title="Edit request"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
        {!r.actioned_at && isCreatorOrAdmin(r) && r.status === "pending" && (
          <button
            className="p-1.5 rounded-md text-amber-600 hover:bg-amber-50"
            onClick={(e) => { e.stopPropagation(); nudge(r); }}
            data-testid={`repair-nudge-${r.id}`}
            title="Nudge the approver"
          >
            <BellRing className="w-3.5 h-3.5" />
          </button>
        )}
        {!r.actioned_at && isCreatorOrAdmin(r) && (r.status === "denied" || r.status === "revisit") && (
          <button
            className="p-1.5 rounded-md text-summit-primary hover:bg-summit-glacier"
            onClick={(e) => { e.stopPropagation(); setEditResend(r); }}
            data-testid={`repair-edit-resend-${r.id}`}
            title="Edit & resend to approver"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
        {!r.actioned_at && r.status !== "pending" && canAction(r) && (
          <button
            className="p-1.5 rounded-md text-gray-500 hover:bg-summit-glacier"
            onClick={(e) => { e.stopPropagation(); setActioned(r, true); }}
            data-testid={`repair-action-${r.id}`}
            title="Move to Actioned estimates"
          >
            <Archive className="w-3.5 h-3.5" />
          </button>
        )}
        {r.actioned_at && canAction(r) && (
          <button
            className="p-1.5 rounded-md text-gray-500 hover:bg-summit-glacier"
            onClick={(e) => { e.stopPropagation(); setActioned(r, false); }}
            data-testid={`repair-unaction-${r.id}`}
            title="Restore to active estimates"
          >
            <ArchiveRestore className="w-3.5 h-3.5" />
          </button>
        )}
        {(user.role === "admin" || (r.created_by === user.id && r.status === "pending")) && (
          <button
            className="p-1.5 rounded-md text-rose-600 hover:bg-rose-50"
            onClick={(e) => { e.stopPropagation(); remove(r); }}
            data-testid={`repair-delete-${r.id}`}
            title="Delete request"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </td>
    </tr>
  );

  const COLS = 6;

  return (
    <div className="w-full mx-auto px-4 sm:px-[15%] py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Repair Approvals · MST</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-1" data-testid="repair-heading">Repair estimates</h1>
          <p className="text-sm text-gray-500 mt-1">You see your own requests, plus your reports&apos; and their reports&apos; — approvers review from Tasks.</p>
        </div>
        <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={() => setShowNew(true)} data-testid="repair-new-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New request
        </Button>
      </div>

      {awaitingMe.length > 0 && (
        <div className="mt-8 border border-amber-200 bg-amber-50/60 rounded-lg p-4" data-testid="repair-awaiting-me">
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-800">Awaiting your decision · {awaitingMe.length}</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {awaitingMe.map((r) => (
              <button key={r.id} onClick={() => setReviewId(r.id)}
                className="px-3 py-1.5 rounded-md bg-white border border-amber-200 text-sm hover:bg-amber-100 transition-colors inline-flex items-center gap-2"
                data-testid={`repair-awaiting-${r.id}`}>
                {r.description} <span className="font-mono text-[10px] text-gray-500">· {r.created_by_name}</span>
                <DueCountdown dueDate={r.due_date} status={r.status} />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setTab("active")}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === "active" ? "bg-summit-primary text-white" : "bg-white border border-summit-hairline text-gray-600 hover:bg-summit-glacier"}`}
          data-testid="repair-tab-active"
        >
          Active · {activeItems.length}
        </button>
        <button
          onClick={() => setTab("actioned")}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === "actioned" ? "bg-summit-primary text-white" : "bg-white border border-summit-hairline text-gray-600 hover:bg-summit-glacier"}`}
          data-testid="repair-tab-actioned"
        >
          Actioned · {actionedItems.length}
          {freshCount > 0 && (
            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-mono text-[9px] uppercase tracking-widest" data-testid="repair-actioned-fresh-dot">
              {freshCount} new
            </span>
          )}
        </button>
        {tab === "actioned" && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400" data-testid="repair-actioned-note">
            Actioned estimates auto-delete after 30 days
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {anyFilter && (
            <Button size="sm" variant="ghost" className="h-8 text-xs text-gray-500" onClick={() => { setSearch(""); setFilters({}); }} data-testid="repair-clear-filters">
              <X className="w-3.5 h-3.5 mr-1" /> Clear filters
            </Button>
          )}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input className="h-8 pl-8 w-56 text-sm" placeholder="Search estimates…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="repair-search" />
          </div>
        </div>
      </div>

      <div className="mt-4 border border-summit-hairline rounded-lg bg-white overflow-hidden">
        <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="bg-summit-canvas border-b border-summit-hairline">
                {TABLE_COLS.map((col) => (
                  <th key={col.label || "actions"} className={`text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 ${col.width}`}>
                    {col.label}
                    {col.keys.length > 0 && (
                      <HeaderFilterPopover
                        col={col}
                        filterDefs={FILTER_DEFS}
                        filters={filters}
                        setFilter={(k, v) => setFilters((f) => ({ ...f, [k]: v }))}
                        nameOptions={nameOptions}
                        testPrefix="repair"
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="repair-table-body">
              {items === null ? (
                <tr><td colSpan={COLS} className="px-3 py-8 text-center text-gray-400 text-sm">Loading…</td></tr>
              ) : visibleItems.length === 0 ? (
                <tr><td colSpan={COLS} className="px-3 py-8 text-center text-gray-400 text-sm" data-testid="repair-empty">
                  {tab === "actioned" ? "No actioned estimates." : "No repair approval requests yet."}
                </td></tr>
              ) : (
                groups.map(({ cs, fleets }) => (
                  <React.Fragment key={cs}>
                    <tr className={cs === "Yes" ? "bg-rose-50" : "bg-summit-glacier"} data-testid={`repair-group-${cs.toLowerCase()}`}>
                      <td colSpan={COLS} className={`px-3 py-1.5 font-display text-xs uppercase tracking-widest ${cs === "Yes" ? "text-rose-800" : "text-gray-600"}`}>
                        {cs === "Yes" && <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}
                        Critical spares · {cs}
                      </td>
                    </tr>
                    {fleets.map(([fleet, rows]) => (
                      <React.Fragment key={fleet}>
                        <tr className="bg-summit-canvas/60" data-testid={`repair-fleet-${cs.toLowerCase()}-${fleet.replace(/\s+/g, "-").toLowerCase()}`}>
                          <td colSpan={COLS} className="px-3 py-1 pl-6 font-mono text-[10px] uppercase tracking-widest text-gray-500">
                            Fleet · {fleet} ({rows.length})
                          </td>
                        </tr>
                        {rows.map((r) => <Row key={r.id} r={r} />)}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        {visibleItems.length > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 border-t border-summit-hairline bg-summit-canvas/60 px-3 py-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500" data-testid="repair-page-label">
              Page {safePage} of {totalPages} · {visibleItems.length} estimates
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} data-testid="repair-page-prev">Previous</Button>
              <Button size="sm" variant="outline" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)} data-testid="repair-page-next">Next</Button>
            </div>
          </div>
        )}
      </div>

      <RequestFormDialog open={showNew} onOpenChange={setShowNew} approvers={approvers} onSaved={load} />
      <RequestFormDialog
        open={!!editResend}
        onOpenChange={(o) => !o && setEditResend(null)}
        approvers={approvers}
        onSaved={load}
        resendReq={editResend}
      />
      <RequestFormDialog
        open={!!editPending}
        onOpenChange={(o) => !o && setEditPending(null)}
        approvers={approvers}
        onSaved={load}
        editReq={editPending}
      />
      {reviewReq && (
        <RepairReviewDialog
          req={reviewReq}
          canDecide={reviewReq.approver_id === user.id || user.role === "admin"}
          canNudge={isCreatorOrAdmin(reviewReq)}
          onOpenChange={() => setReviewId(null)}
          onDecided={load}
          onResend={isCreatorOrAdmin(reviewReq) ? () => {} : undefined}
        />
      )}
    </div>
  );
}
